import {
  BidV2Status,
  type MinimumRequirementDto,
  type ReceivedBidV2Dto,
} from "../../../../autogen/bff-api";
import { effectiveMinimumRequirementVerdict } from "../bid-detail/bidMinimumRequirements";

export type MinimumRequirementState = "FULFILLED" | "NOT_FULFILLED" | "PENDING" | "REJECTED";

export interface MinimumRequirementRow {
  bidId: string;
  organizationId: string;
  supplierName: string;
  state: MinimumRequirementState;
  reviewed: number;
  total: number;
}

export interface MinimumRequirementReviewSummary {
  rows: MinimumRequirementRow[];
  fulfilled: number;
  notFulfilled: number;
  pending: number;
  assessable: number;
  assessed: number;
  isResolved: boolean;
}

export const deriveMinimumRequirementReview = (
  requirements: MinimumRequirementDto[],
  bids: ReceivedBidV2Dto[],
): MinimumRequirementReviewSummary => {
  const rows = bids.map((bid) => toRow(requirements, bid));
  const counts = (state: MinimumRequirementState) => rows.filter((row) => row.state === state).length;
  const pending = counts("PENDING");
  const assessable = rows.length - counts("REJECTED");
  return {
    rows,
    fulfilled: counts("FULFILLED"),
    notFulfilled: counts("NOT_FULFILLED"),
    pending,
    assessable,
    assessed: assessable - pending,
    isResolved: rows.length > 0 && pending === 0,
  };
};

const toRow = (
  requirements: MinimumRequirementDto[],
  bid: ReceivedBidV2Dto,
): MinimumRequirementRow => {
  const byId = new Map(
    (bid.minimumRequirementResponses ?? []).map(
      (response) => [response.minimumRequirementId, response] as const,
    ),
  );
  const verdicts = requirements.map((requirement) =>
    effectiveMinimumRequirementVerdict(byId.get(requirement.id ?? "")),
  );
  return {
    bidId: bid.id,
    organizationId: bid.owner,
    supplierName: bid.ownerName,
    state: deriveState(bid, verdicts),
    reviewed: verdicts.filter((verdict) => verdict != null).length,
    total: requirements.length,
  };
};

const deriveState = (
  bid: ReceivedBidV2Dto,
  verdicts: (boolean | null)[],
): MinimumRequirementState => {
  if (bid.status === BidV2Status.Rejected) return "REJECTED";
  if (verdicts.some((verdict) => verdict === false)) return "NOT_FULFILLED";
  return verdicts.every((verdict) => verdict === true) ? "FULFILLED" : "PENDING";
};
